import type { ReactNode } from "react";
import { IndicatorCard, ProgressBar } from "../../components/index.js";
import type { LeadTimeSummary } from "../../types.js";

/**
 * Card 1 — Lead Time médio (Design.md §5.2). TODOS os números são REAIS
 * (GET /api/v1/lead-time): número grande = média do lead time CONFIGURADO dos
 * fornecedores (Cadastro › Fornecedores), e a lista abaixo detalha o lead time
 * por fornecedor (menor → maior), com o menor/maior no rodapé.
 *
 * Nota de fidelidade (Design.md §5.2): o sistema NÃO mede a duração de cada
 * estágio operacional (Fornecedor → Prep Center → Elegível p/ venda) — não há
 * carimbos de transição por estágio no modelo de dados. Por isso a antiga "barra
 * de 3 estágios" (com durações fixas 2/3/5 dias) foi substituída por este
 * detalhamento real e configurável. Quando não há fornecedor cadastrado, o card
 * mostra um estado vazio honesto em vez de números fabricados.
 */
export interface LeadTimeCardProps {
  leadTime: LeadTimeSummary | null;
}

export function LeadTimeCard({ leadTime }: LeadTimeCardProps): ReactNode {
  const suppliers = leadTime?.suppliers ?? [];
  const hasData = leadTime !== null && suppliers.length > 0;
  const value = hasData ? `${leadTime.averageLeadTimeDays} dias` : "—";
  const maxLead = suppliers.reduce(
    (max, s) => (s.leadTimeDays > max ? s.leadTimeDays : max),
    0
  );

  return (
    <IndicatorCard
      title="Lead Time médio"
      value={value}
      detail={
        hasData
          ? `média de ${suppliers.length} ${
              suppliers.length === 1 ? "fornecedor" : "fornecedores"
            }`
          : undefined
      }
    >
      {hasData ? (
        <>
          <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
            {suppliers.map((s) => (
              <ProgressBar
                key={s.id}
                value={s.leadTimeDays}
                max={maxLead}
                tone="blue"
                label={s.name}
                trailing={`${s.leadTimeDays} ${
                  s.leadTimeDays === 1 ? "dia" : "dias"
                }`}
              />
            ))}
          </div>
          {leadTime.minLeadTimeDays !== null &&
          leadTime.maxLeadTimeDays !== null ? (
            <div
              style={{
                marginTop: "0.7rem",
                fontSize: 11.5,
                color: "var(--text-muted)"
              }}
            >
              Menor: {leadTime.minLeadTimeDays} dias · Maior:{" "}
              {leadTime.maxLeadTimeDays} dias
            </div>
          ) : null}
        </>
      ) : (
        <p style={{ fontSize: 11.5, color: "var(--text-muted)" }}>
          Cadastre fornecedores com lead time em Cadastro › Fornecedores para ver
          a média e o detalhamento por fornecedor.
        </p>
      )}
    </IndicatorCard>
  );
}
